import type { StandardServicePageContent } from "~/content/servicePage";

export const LightningProtectionContent: StandardServicePageContent = {
  heroTitle: "Przegląd instalacji odgromowej",
  quickFactsTitle: "Najważniejsze informacje w skrócie",
  quickFacts: [
    {
      label: "Jak często",
      value: "Minimum co 5 lat",
      description:
        "To element obowiązkowej kontroli okresowej z Prawa budowlanego. Dodatkowy przegląd warto zrobić po burzy, remoncie dachu, ociepleniu albo montażu fotowoltaiki.",
    },
    {
      label: "Co jest badane",
      value: "Zwody, przewody, uziom i połączenia",
      description:
        "Kontrola obejmuje oględziny elementów piorunochronu i pomiary potwierdzające ciągłość połączeń oraz stan układu uziemiającego.",
    },
    {
      label: "Jaki dokument",
      value: "Protokół z pomiarami i oceną stanu instalacji",
      description:
        "Dokument porządkuje stan techniczny systemu i ułatwia planowanie napraw, dalszych pomiarów albo uzupełnienie dokumentacji budynku.",
    },
    {
      label: "Ile kosztuje",
      value: "Dom zwykle od około 300-700 zł",
      description:
        "Na cenę wpływa wysokość i wielkość budynku, liczba przewodów odprowadzających, dostęp do dachu i złączy kontrolnych oraz lokalizacja usługi.",
    },
  ],
  faqTitle: "Najczęstsze pytania o przegląd instalacji odgromowej",
  sections: [
    {
      title: "Na czym polega przegląd instalacji odgromowej",
      paragraphs: [
        `Przegląd instalacji odgromowej polega na sprawdzeniu, czy system piorunochronny nadal jest zdolny do bezpiecznego przejęcia energii wyładowania i odprowadzenia jej do ziemi. Sama obecność zwodów na dachu nie oznacza jeszcze, że instalacja działa prawidłowo. Liczy się stan połączeń, ciągłość przewodów odprowadzających, jakość mocowań i skuteczność całego układu ochronnego.`,
        `Kontrola łączy oględziny z pomiarami. Oględziny pozwalają wychwycić korozję, poluzowane elementy, mechaniczne uszkodzenia, brakujące odcinki instalacji albo ślady ingerencji po pracach dachowych, elewacyjnych czy montażu nowych urządzeń. Pomiary pokazują z kolei, czy układ uziemiający oraz połączenia zachowują parametry potrzebne do skutecznej ochrony budynku.`,
        `Dobrze wykonany przegląd kończy się protokołem, który porządkuje stan instalacji odgromowej i wskazuje, czy system może być dalej bezpiecznie eksploatowany, czy wymaga naprawy, rozbudowy albo ponownego badania po usunięciu usterek.`,
      ],
    },
    {
      title: "Kiedy kontrola piorunochronu jest obowiązkowa i kiedy zrobić ją wcześniej",
      paragraphs: [
        `Kontrola instalacji odgromowej jest elementem obowiązkowej kontroli okresowej wykonywanej co najmniej raz na 5 lat. Wynika to z Prawa budowlanego, które nakłada na właściciela lub zarządcę obowiązek badania stanu technicznego i przydatności do użytkowania instalacji elektrycznej oraz piorunochronnej.`,
        `Poza tym terminem przegląd warto wykonać również każdorazowo wtedy, gdy istnieje ryzyko pogorszenia stanu instalacji. Dotyczy to między innymi sytuacji po wyładowaniu atmosferycznym, po remoncie dachu, elewacji lub ociepleniu, po montażu fotowoltaiki, klimatyzatorów czy innych urządzeń zmieniających układ elementów na dachu.`,
        `Dodatkowa kontrola jest uzasadniona także wtedy, gdy nie ma aktualnej dokumentacji, nie wiadomo kiedy wykonano ostatnie pomiary albo podczas oględzin widać zerwane przewody, korozję, luźne uchwyty albo uszkodzone złącza kontrolne.`,
      ],
    },
    {
      title: "Co obejmują oględziny i pomiary instalacji odgromowej",
      paragraphs: [
        `Kontrola instalacji odgromowej obejmuje ocenę zwodów, przewodów odprowadzających, złączy kontrolnych, połączeń wyrównawczych, uziomu oraz punktów, w których instalacja łączy się z konstrukcją budynku. Technik sprawdza, czy system jest kompletny, czy nie ma śladów korozji, pęknięć, poluzowanych połączeń albo uszkodzeń po wcześniejszych pracach budowlanych.`,
        `Ważna jest też ocena przebiegu instalacji i jej zgodności z aktualnym stanem obiektu. Częstym problemem są późniejsze przeróbki dachu lub elewacji, przez które przewody odprowadzające zostają przesunięte, zakryte albo uszkodzone. Taka instalacja może wyglądać poprawnie z daleka, ale nie zapewniać już realnej ochrony.`,
        `Końcowa ocena nie opiera się wyłącznie na wzrokowym sprawdzeniu. Żeby rzetelnie potwierdzić sprawność systemu, oględziny trzeba połączyć z pomiarami i odnieść wyniki do rodzaju budynku, sposobu jego użytkowania oraz zmian, które zaszły od ostatniej kontroli.`,
      ],
    },
    {
      title: "Cena przeglądu instalacji odgromowej",
      paragraphs: [
        `Cena przeglądu instalacji odgromowej zależy od wielkości i wysokości budynku, liczby przewodów odprowadzających, dostępności dachu oraz zakresu pomiarów. Dom jednorodzinny wycenia się inaczej niż blok, hala albo obiekt z rozbudowaną infrastrukturą techniczną.`,
        `Na koszt wpływa także to, czy jest to standardowa kontrola okresowa, czy badanie po burzy, remoncie dachu, montażu PV albo po zmianach w samej instalacji. Im trudniejszy dostęp do złączy kontrolnych i uziomu oraz im szersza dokumentacja, tym większy nakład pracy po stronie wykonawcy.`,
        `Porównując oferty, warto sprawdzić, czy cena obejmuje pełne oględziny, pomiary i protokół z wnioskami. W praktyce lokalny rynek i dojazd mają znaczenie, ale jeszcze ważniejsze jest to, czy usługa kończy się realną oceną stanu ochrony odgromowej, a nie tylko pojedynczym odczytem.`,
      ],
    },
  ],
  scopeTitle: "Zakres usług przeglądu instalacji odgromowej",
  scopeItems: [
    "Oględziny zwodów",
    "Kontrola przewodów odprowadzających",
    "Pomiar rezystancji uziemienia",
    "Sprawdzenie połączeń wyrównawczych",
    "Ocena stanu uziomów",
    "Kontrola złączy kontrolnych",
    "Weryfikacja śladów korozji",
    "Protokół z pomiarami",
  ],
  seo: {
    title: "Przegląd instalacji odgromowej: co 5 lat, pomiary i protokół",
    description:
      "Zamów przegląd instalacji odgromowej dla domu, wspólnoty lub firmy. Sprawdź obowiązek co 5 lat, zakres pomiarów i to, co zawiera protokół piorunochronu.",
  },
};
